/**
 * /solo/preview — PUBLIC Sample Day preview (no auth).
 *
 *  Shows a Day 1 Game Plan mockup + a fully-readable Unit 1 lesson so prospective
 *  families can taste the SOLO experience before they enroll. Every surface has
 *  a bold "Enroll Now" CTA pointing to /solo/enroll.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import {
  Loader2, ArrowRight, Trophy, Flame, Clock, Target, BookOpen, ChevronRight, Check,
  Sparkles, Play, ShieldCheck, Lock,
} from 'lucide-react';
import api from '../services/api';

const SUBJECT_ICON = { math: '∑', ela: '✎', science: '⚛', social_studies: '🌐' };
const SUBJECT_LABEL = { math: 'Math', ela: 'ELA', science: 'Science', social_studies: 'Social Studies' };

const SoloPreview = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [lesson, setLesson] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);

  const openLesson = useCallback(async (lessonId) => {
    setActiveLessonId(lessonId);
    setLesson(null);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setLessonLoading(true);
    try {
      const r = await api.get(`/academy/solo/preview/lesson/${encodeURIComponent(lessonId)}`);
      setLesson(r.data.lesson);
      setTimeout(() => document.getElementById('preview-lesson')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    } catch { setLesson(null); }
    finally { setLessonLoading(false); }
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/academy/solo/preview/sample-day');
      setData(r.data);
      const first = (r.data.game_plan || []).find((g) => g.lesson.has_body);
      if (first) openLesson(first.lesson.id);
    } catch { /* empty preview shows enroll CTA anyway */ }
  }, [openLesson]);
  useEffect(() => { load(); }, [load]);

  if (!data) return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
    </div>
  );

  const stats = data.stats_mock || {};
  const minutesPct = Math.min(100, Math.round(100 * stats.minutes_today / stats.state_min_minutes));

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pb-24" data-testid="solo-preview">
      {/* HERO */}
      <div className="border-b-2 border-zinc-900 pt-24 pb-10 px-6" style={{ background: 'linear-gradient(to bottom, rgba(255,87,34,0.10), transparent)' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">SAA SOLO · SAMPLE DAY</div>
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight leading-[1.02] mt-2">
            One Day in a <span className="text-orange-500">Scholar Athlete's</span> School
          </h1>
          <p className="text-zinc-300 text-base md:text-lg mt-4 max-w-2xl leading-relaxed">
            No sign-up needed. Read a real lesson. Take a real quiz. Then decide if this is the school for your family.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <button onClick={() => navigate('/solo/enroll')}
              className="bg-orange-500 hover:bg-orange-400 text-black px-6 py-3 text-[11px] font-black uppercase tracking-[0.2em] flex items-center gap-2"
              data-testid="hero-enroll">
              Enroll Now · ${data.pricing?.track_b || 299}/mo <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <a href="#preview-lesson"
              onClick={(e) => { e.preventDefault(); document.getElementById('preview-lesson')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="border-2 border-zinc-700 hover:border-white text-white px-6 py-3 text-[11px] font-black uppercase tracking-[0.2em] flex items-center gap-2"
              data-testid="hero-jump-lesson">
              <Play className="w-3.5 h-3.5" /> Jump to the Lesson
            </a>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-6 py-10 space-y-12">
        {/* SIMULATED DASHBOARD */}
        <section>
          <SectionTitle>The Athlete's Dashboard · Day 1 of 180</SectionTitle>

          {/* GAME PLAN cards */}
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-2">Game Plan</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {(data.game_plan || []).map((gp) => {
              const active = activeLessonId === gp.lesson.id;
              const readable = gp.lesson.has_body;
              return (
                <button key={gp.course.id}
                  onClick={() => readable && openLesson(gp.lesson.id)}
                  disabled={!readable}
                  className={`text-left p-4 border-2 transition-all ${
                    active ? 'border-orange-500 bg-orange-500/10' :
                    readable ? 'border-zinc-800 hover:border-orange-500 bg-zinc-950' :
                    'border-zinc-900 bg-zinc-950 opacity-60 cursor-not-allowed'
                  }`}
                  data-testid={`preview-card-${gp.course.subject}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-2xl font-black text-orange-500">{SUBJECT_ICON[gp.course.subject] || '•'}</div>
                    {readable ? (
                      <div className="text-[9px] font-black uppercase tracking-widest text-zinc-500">{gp.lesson.minutes_est || 20}m</div>
                    ) : (
                      <Lock className="w-3.5 h-3.5 text-zinc-500" />
                    )}
                  </div>
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">{SUBJECT_LABEL[gp.course.subject] || gp.course.subject}</div>
                  <div className="text-sm font-black leading-tight text-white line-clamp-2">{gp.lesson.title}</div>
                  <div className="text-[10px] text-zinc-500 mt-2 line-clamp-1">{gp.lesson.unit_title}</div>
                  <div className={`mt-3 flex items-center gap-1 text-[10px] font-black uppercase tracking-widest ${readable ? 'text-orange-500' : 'text-zinc-600'}`}>
                    {readable ? <>Preview <ChevronRight className="w-3 h-3" /></> : 'Enroll to Unlock'}
                  </div>
                </button>
              );
            })}
          </div>

          {/* STAT SHEET mock */}
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-2">Stat Sheet · What Your Scholar Sees</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBig icon={<Clock className="w-4 h-4" />} label="Minutes" value={`${stats.minutes_today}`} sub={`/ ${stats.state_min_minutes}`} pct={minutesPct} />
            <StatBig icon={<Flame className="w-4 h-4" />} label="Streak" value={`${stats.streak_days}`} sub="days" />
            <StatBig icon={<Target className="w-4 h-4" />} label="Mastery" value={`${stats.mastery_pct}%`} sub="avg score" />
            <StatBig icon={<Trophy className="w-4 h-4" />} label="Completed" value={`${stats.lessons_completed}`} sub="lessons" />
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-black italic">
            Illustrative — real dashboards populate live from your student's work.
          </div>
        </section>

        {/* LESSON */}
        <section id="preview-lesson">
          <SectionTitle>Today's Lesson · Fully Readable</SectionTitle>
          {lessonLoading ? (
            <div className="border-2 border-zinc-900 bg-zinc-950 p-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
            </div>
          ) : lesson ? (
            <LessonPreview lesson={lesson}
              quizAnswers={quizAnswers} setQuizAnswers={setQuizAnswers}
              quizSubmitted={quizSubmitted} setQuizSubmitted={setQuizSubmitted}
              onEnroll={() => navigate('/solo/enroll')} />
          ) : (
            <div className="border-2 border-zinc-900 bg-zinc-950 p-8 text-center">
              <div className="text-sm text-zinc-400">Pick a subject above to preview its lesson.</div>
            </div>
          )}
        </section>

        {/* CLOSING CTA */}
        <section className="border-2 border-orange-500 bg-orange-500/5 p-8 text-center">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500 mb-3">Ready to Enroll?</div>
          <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.05]">
            180 Days. 4 Subjects. <br className="hidden md:block" />One Head Coach.
          </h2>
          <p className="text-zinc-300 max-w-xl mx-auto mt-4">
            California, Texas, and Florida standards. Sports-themed lessons. Cap at one week ahead. Cancel anytime.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <button onClick={() => navigate('/solo/enroll')}
              className="bg-orange-500 hover:bg-orange-400 text-black px-6 py-3 text-[11px] font-black uppercase tracking-[0.2em] flex items-center gap-2"
              data-testid="footer-enroll-a">
              <ShieldCheck className="w-3.5 h-3.5" /> Track A · School of Record · $399/mo
            </button>
            <button onClick={() => navigate('/solo/enroll')}
              className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-black px-6 py-3 text-[11px] font-black uppercase tracking-[0.2em] flex items-center gap-2"
              data-testid="footer-enroll-b">
              Track B · Charter Partnership · $299/mo
            </button>
          </div>
          <Link to="/solo/enroll" className="mt-6 inline-block text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white font-black">
            See all track details →
          </Link>
        </section>
      </div>
    </div>
  );
};

const SectionTitle = ({ children }) => (
  <h2 className="text-[10px] tracking-[0.4em] uppercase font-black text-white mb-4">{children}</h2>
);

const StatBig = ({ icon, label, value, sub, pct }) => (
  <div className="border-2 border-zinc-900 bg-zinc-950 p-4">
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 font-black flex items-center gap-1.5">{icon} {label}</div>
    <div className="text-3xl font-black text-white mt-1.5">{value}</div>
    <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>
    {pct != null && (
      <div className="w-full h-1 bg-black mt-3">
        <div className="h-full bg-orange-500" style={{ width: `${pct}%` }} />
      </div>
    )}
  </div>
);

const LessonPreview = ({ lesson, quizAnswers, setQuizAnswers, quizSubmitted, setQuizSubmitted, onEnroll }) => {
  const quiz = lesson.quiz || [];
  const correct = quiz.reduce((n, q, i) => n + (quizAnswers[i] === q.answer_index ? 1 : 0), 0);
  const scorePct = quiz.length ? Math.round(100 * correct / quiz.length) : 0;

  return (
    <div className="border-2 border-zinc-900 bg-zinc-950 p-6 md:p-8" data-testid="lesson-preview">
      <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500">{lesson.subject}</div>
      <h3 className="text-2xl md:text-4xl font-black uppercase tracking-tight leading-[1.05] mt-2">{lesson.title}</h3>
      <div className="text-[11px] text-zinc-500 mt-2">{lesson.unit_title} · {lesson.minutes_est || 20} min</div>

      <div className="mt-6 max-w-none text-zinc-300 leading-relaxed space-y-3 [&_h1]:text-2xl [&_h1]:font-black [&_h1]:uppercase [&_h1]:tracking-tight [&_h1]:text-white [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-lg [&_h2]:font-black [&_h2]:uppercase [&_h2]:tracking-wide [&_h2]:text-orange-500 [&_h2]:mt-4 [&_h2]:mb-1 [&_h3]:text-base [&_h3]:font-black [&_h3]:text-white [&_h3]:mt-3 [&_p]:leading-relaxed [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5 [&_li]:my-1 [&_strong]:text-orange-400 [&_strong]:font-black [&_code]:bg-black [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-orange-400 [&_code]:text-sm">
        <ReactMarkdown>{lesson.body_md}</ReactMarkdown>
      </div>

      {(lesson.vocab || []).length > 0 && (
        <div className="border-2 border-zinc-900 bg-black p-5 mt-6">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mb-3 flex items-center gap-2">
            <BookOpen className="w-3.5 h-3.5" /> Playbook Vocabulary
          </div>
          <ul className="space-y-2">
            {lesson.vocab.map((v, i) => (
              <li key={i} className="text-sm text-zinc-300 leading-relaxed">{v}</li>
            ))}
          </ul>
        </div>
      )}

      {quiz.length > 0 && (
        <div className="border-2 border-orange-500/40 bg-orange-500/5 p-5 mt-6" data-testid="preview-quiz">
          <div className="text-[10px] tracking-[0.4em] uppercase font-black text-orange-500 mb-4">Take the Quiz · {quiz.length} Questions</div>
          <div className="space-y-6">
            {quiz.map((q, qi) => (
              <div key={qi}>
                <div className="text-sm font-black text-white mb-3">{qi + 1}. {q.q}</div>
                <div className="grid grid-cols-1 gap-2">
                  {(q.choices || []).map((c, ci) => {
                    const chosen = quizAnswers[qi] === ci;
                    const isCorrect = ci === q.answer_index;
                    const show = quizSubmitted;
                    const right = show && chosen && isCorrect;
                    const wrong = show && chosen && !isCorrect;
                    const revealCorrect = show && isCorrect && !chosen;
                    return (
                      <button key={ci} disabled={quizSubmitted}
                        onClick={() => setQuizAnswers({ ...quizAnswers, [qi]: ci })}
                        className={`text-left px-3 py-2.5 border-2 text-sm flex items-center gap-2 transition-all ${
                          right ? 'border-green-500 bg-green-500/10' :
                          wrong ? 'border-red-500 bg-red-500/10' :
                          revealCorrect ? 'border-green-500/50 text-green-400' :
                          chosen ? 'border-orange-500 bg-orange-500/10 text-white' :
                          'border-zinc-800 hover:border-zinc-600 text-zinc-300 bg-black'
                        }`}
                        data-testid={`preview-q${qi}-c${ci}`}>
                        {right || revealCorrect ? <Check className="w-3.5 h-3.5 flex-shrink-0" /> : <span className="w-3.5" />}
                        {c}
                      </button>
                    );
                  })}
                </div>
                {quizSubmitted && q.explanation && (
                  <div className="text-[11px] text-zinc-400 mt-2 italic">{q.explanation}</div>
                )}
              </div>
            ))}
          </div>

          {!quizSubmitted ? (
            <button onClick={() => setQuizSubmitted(true)}
              disabled={Object.keys(quizAnswers).length !== quiz.length}
              className="w-full bg-orange-500 hover:bg-orange-400 text-black py-3 text-[11px] font-black uppercase tracking-[0.2em] mt-6 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="preview-quiz-submit">
              Submit Quiz
            </button>
          ) : (
            <div className="mt-6 border-t-2 border-orange-500/30 pt-5 text-center">
              <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500">Your Score</div>
              <div className="text-5xl font-black text-orange-500 mt-1">{scorePct}%</div>
              <p className="text-sm text-zinc-300 mt-4 max-w-md mx-auto">
                {scorePct >= 80 ? "That's mastery. Coach B would say: keep the tempo." : "Not there yet — that's what the Film Room is for."}
              </p>
              <button onClick={onEnroll}
                className="mt-6 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-black px-6 py-3 text-[11px] font-black uppercase tracking-[0.2em]"
                data-testid="post-quiz-enroll">
                Enroll to Keep Playing <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SoloPreview;
